import React from 'react';
import {
  Box,
  ButtonBase,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ChevronRightRounded as ChevronRightIcon } from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import { getTimelineMetaForCategory } from '../../constants/logTypeRegistry';
import {
  ASK_ACCENT,
  ASK_ACCENT_DARK,
} from './askRecordUi';

const AskRecordSourceCard = ({
  compact = false,
  expanded = false,
  index,
  onToggle,
  source,
}) => {
  const meta = getTimelineMetaForCategory(source?.category || 'note');
  const Icon = meta.iconComponent;
  const color = meta.color || ASK_ACCENT_DARK;
  const iconSize = compact ? 28 : 36;
  const sourceMeta = [source?.sourceGroup === 'profile' ? (source?.sourceLabel || 'Care Profile fact') : '', source?.authorName || (source?.sourceGroup === 'profile' ? '' : source?.sourceLabel), source?.dateLabel]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(' · ');

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: compact ? '14px' : '18px',
        bgcolor: compact ? alpha(colors.brand.tint, 0.08) : '#EEF4F1',
        border: `1px solid ${alpha(colors.brand.tint, 0.14)}`,
        overflow: 'hidden',
        boxShadow: compact ? `0 6px 14px ${alpha(colors.semantic.ink, 0.04)}` : 'none',
        '&:hover': {
          bgcolor: alpha(colors.brand.tint, 0.1),
        },
      }}
    >
      <ButtonBase
        component="div"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={`Source ${index + 1}: ${source?.title || meta.label || 'Log entry'}`}
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: compact ? '26px 30px minmax(0, 1fr) auto' : '38px 44px minmax(0, 1fr) auto',
          alignItems: 'center',
          gap: compact ? 0.5 : 0.8,
          p: compact ? 0.58 : 0.82,
          textAlign: 'left',
        }}
      >
        <Box
          sx={{
            width: compact ? 23 : 32,
            height: compact ? 23 : 32,
            borderRadius: '999px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(ASK_ACCENT, 0.12),
            color: ASK_ACCENT_DARK,
            fontSize: compact ? '0.72rem' : '0.88rem',
            fontWeight: 500,
          }}
        >
          {index + 1}
        </Box>
        <Box
          sx={{
            width: iconSize,
            height: iconSize,
            borderRadius: compact ? '10px' : '12px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(color, 0.14),
            color,
          }}
        >
          {Icon ? <Icon sx={{ fontSize: compact ? 16 : 18 }} /> : (
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'inherit' }}>
              {String(meta.label || source?.category || 'L').charAt(0)}
            </Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: compact ? '0.78rem' : '0.94rem', lineHeight: 1.14, fontWeight: 500, color: colors.semantic.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {source?.title || meta.label || 'Log entry'}
          </Typography>
          <Typography sx={{ mt: 0.12, fontSize: compact ? '0.66rem' : '0.8rem', lineHeight: 1.12, fontWeight: 500, color: alpha(colors.semantic.ink, 0.58), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {sourceMeta || source?.summary || ''}
          </Typography>
        </Box>
        <ChevronRightIcon sx={{ fontSize: compact ? 18 : 22, color: alpha(colors.semantic.ink, 0.52), transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 160ms ease' }} />
      </ButtonBase>
      <Collapse in={expanded} timeout={160} unmountOnExit>
        <Box sx={{ px: compact ? 0.72 : 1, pb: compact ? 0.7 : 0.95, pl: compact ? 6.5 : 9.4 }}>
          <Typography sx={{ fontSize: compact ? '0.74rem' : '0.84rem', lineHeight: 1.34, fontWeight: 400, color: alpha(colors.semantic.ink, 0.72) }}>
            {source?.summary || source?.title || 'No excerpt available.'}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

const AskRecordSourceList = ({
  compact = false,
  maxSources = 3,
  sources = [],
}) => {
  const [expandedSourceKey, setExpandedSourceKey] = React.useState(null);
  const visibleSources = sources.slice(0, maxSources);
  if (!visibleSources.length) return null;

  return (
    <Stack spacing={compact ? 0.5 : 0.55} sx={{ mt: compact ? 0.75 : 0.85 }}>
      {visibleSources.map((source, index) => (
        <AskRecordSourceCard
          key={source.sourceId || source.id || index}
          compact={compact}
          expanded={expandedSourceKey === (source.sourceId || source.id || index)}
          index={index}
          onToggle={() => {
            const sourceKey = source.sourceId || source.id || index;
            setExpandedSourceKey((currentKey) => (currentKey === sourceKey ? null : sourceKey));
          }}
          source={source}
        />
      ))}
    </Stack>
  );
};

export default AskRecordSourceList;
